import React, { useState } from "react";
import { Spinner } from "react-bootstrap";

const TransactionsTableSection = ({
  loadingTxs,
  unsettledTxs,
  selectedLogs,
  commType,
  commVal,
  onToggle,
  onSelectAll,
  selectedItems,
  totalGrossAmount,
  totalCommissionAmount,
}) => {
  // 🎯 State to track expanded project rows
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleExpandRow = (txId) => {
    setExpandedRows((prev) =>
      prev.includes(txId) ? prev.filter((id) => id !== txId) : [...prev, txId]
    );
  };

  return (
    <div className="bma-card">
      <div className="bma-card-body">
        <h2 className="bma-card-title mb-3">3. Select Projects to Settle</h2>
        {loadingTxs ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="danger" />
          </div>
        ) : (
          /* 🎯 VERTICAL SCROLLBAR CONTAINER WITH FIXED MAX HEIGHT */
          <div
            className="table-responsive"
            style={{
              maxHeight: "450px", // Fixed height box for vertical scrolling
              overflowY: "auto",  // Vertical Scrollbar for multiple projects
              overflowX: "auto",
              borderRadius: "8px",
              border: "1px solid #24283b",
              backgroundColor: "#0d1117"
            }}
          >
            <table className="bma-table" style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              {/* 📌 STICKY HEADER */}
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#161b22",
                  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.5)"
                }}
              >
                <tr>
                  <th width="40" style={{ padding: "12px 10px" }}>
                    <input
                      type="checkbox"
                      checked={selectedLogs.length === unsettledTxs.length && unsettledTxs.length > 0}
                      onChange={onSelectAll}
                      disabled={unsettledTxs.length === 0}
                    />
                  </th>
                  <th width="30"></th> {/* Dropdown Arrow Column */}
                  <th>ORDER / REF ID</th>
                  <th>PROJECT</th>
                  <th>ROLE</th>
                  <th>AUDITION / SERVICE</th>
                  <th>TRANSACTION TYPE</th>
                  <th>TRANSACTION DATE</th>
                  <th>TOTAL AMOUNT (₹)</th>
                  <th style={{ color: "#f59e0b" }}>CD COMMISSION (₹)</th>
                </tr>
              </thead>
              <tbody>
                {unsettledTxs.length > 0 ? (
                  unsettledTxs.map((tx) => {
                    const txId = tx.id || tx.payment_log_id;
                    const gross = Number(tx.gross_amount ?? tx.amount ?? 0);
                    const commission = Number(tx.platform_fee ?? tx.commission ?? 0);
                    const isExpanded = expandedRows.includes(txId);
                    const auditionsList = tx.auditions || [];

                    return (
                      <React.Fragment key={txId}>
                        {/* 📌 MAIN PROJECT ROW */}
                        <tr style={{ backgroundColor: isExpanded ? "rgba(255, 255, 255, 0.05)" : "transparent" }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedLogs.includes(txId)}
                              onChange={() => onToggle(txId)}
                            />
                          </td>
                          <td>
                            {/* 🔽 Expand Arrow Button */}
                            {auditionsList.length > 0 && (
                              <button
                                type="button"
                                onClick={() => toggleExpandRow(txId)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#f59e0b",
                                  fontSize: "0.85rem",
                                  cursor: "pointer",
                                  padding: "0 2px"
                                }}
                                title="Click to view audition breakdown"
                              >
                                {isExpanded ? "▼" : "▶"}
                              </button>
                            )}
                          </td>
                          <td style={{ fontFamily: "monospace", fontWeight: "700" }}>
                            {tx.orderId || tx.payment_order_id || `ORD-${txId}`}
                          </td>
                          <td style={{ fontWeight: "700" }}>
                            <span
                              onClick={() => toggleExpandRow(txId)}
                              style={{ cursor: "pointer", color: "#60a5fa", textDecoration: "underline" }}
                              title="Click to expand audition breakdown"
                            >
                              {tx.project || tx.project_name || "Project Not Found"}
                            </span>
                          </td>
                          <td>{tx.role || tx.role_name || "Role Not Found"}</td>
                          <td>{tx.service || "Audition Payment"}</td>
                          <td>{tx.type || "Audition Fee"}</td>
                          <td style={{ color: "#94a3b8" }}>
                            {tx.date ? new Date(tx.date).toLocaleDateString("en-IN") : "—"}
                          </td>
                          <td style={{ fontWeight: "700", color: "#ffffff" }}>
                            ₹ {gross.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ fontWeight: "700", color: "#f59e0b" }}>
                            ₹ {commission.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>

                        {/* 🔽 NESTED BREAKDOWN ROW (WHEN CLICKED/EXPANDED) */}
                        {isExpanded && auditionsList.length > 0 && (
                          <tr style={{ backgroundColor: "rgba(15, 23, 42, 0.85)" }}>
                            <td></td>
                            <td colSpan="9" className="p-3">
                              <div style={{
                                backgroundColor: "#16213e",
                                borderRadius: "8px",
                                padding: "14px",
                                border: "1px solid #1f293d"
                              }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 style={{ color: "#f59e0b", margin: 0, fontSize: "0.85rem", fontWeight: "700" }}>
                                    🎬 Auditions Breakdown for "{tx.project}" ({tx.total_auditions} Auditions Total):
                                  </h6>
                                  <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                                    Project Total Gross: <strong style={{ color: "#4ade80" }}>₹ {gross.toFixed(2)}</strong>
                                  </span>
                                </div>

                                <table className="table table-sm text-light mb-0" style={{ fontSize: "0.82rem" }}>
                                  <thead>
                                    <tr style={{ color: "#94a3b8", borderBottom: "1px solid #334155" }}>
                                      <th>#</th>
                                      <th>ORDER ID</th>
                                      <th>ROLE NAME</th>
                                      <th>PAYMENT DATE</th>
                                      <th className="text-end">AUDITION AMOUNT (₹)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {auditionsList.map((aud, idx) => (
                                      <tr key={aud.id || idx} style={{ borderBottom: "1px solid #1e293b" }}>
                                        <td>{idx + 1}</td>
                                        <td style={{ fontFamily: "monospace", color: "#38bdf8", fontWeight: "600" }}>
                                          {aud.orderId}
                                        </td>
                                        <td>{aud.role}</td>
                                        <td style={{ color: "#94a3b8" }}>
                                          {aud.date ? new Date(aud.date).toLocaleDateString("en-IN") : "—"}
                                        </td>
                                        <td className="text-end" style={{ fontWeight: "700", color: "#4ade80" }}>
                                          ₹ {Number(aud.amount).toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      No unsettled projects found for this Casting Director.
                    </td>
                  </tr>
                )}
              </tbody>

              {/* 📌 STICKY FOOTER TOTALS ROW */}
              {unsettledTxs.length > 0 && (
                <tfoot
                  style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 10,
                    backgroundColor: "#161b22",
                    borderTop: "2px solid #24283b",
                    boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.5)"
                  }}
                >
                  <tr>
                    <td colSpan={7} style={{ fontSize: "0.85rem", color: "#94a3b8", fontWeight: "600", padding: "12px 10px" }}>
                      Showing {selectedItems.length} of {unsettledTxs.length} projects (Total Selected: {selectedItems.length})
                    </td>
                    <td style={{ fontWeight: "800", fontSize: "0.95rem", color: "#4ade80", whiteSpace: "nowrap" }}>
                      ₹ {totalGrossAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ fontWeight: "800", fontSize: "0.95rem", color: "#f59e0b", whiteSpace: "nowrap" }}>
                      ₹ {totalCommissionAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsTableSection;